<template>
  <app-page
    title="Invite employees"
    :subtitle="
      employeesToReinviteCount > 0
        ? `You're offering your employees the best financial wellbeing platform on the market. ${employeesToReinviteCount} of them ${
            employeesToReinviteCount === 1 ? 'is' : 'are'
          } missing out!`
        : ''
    "
    icon="users"
  >
    <!-- Loading State -->
    <div v-if="isLoadingEmployees" class="text-center my-5">
      <b-spinner class="large-spinner" label="Loading..."></b-spinner>
      <p class="mt-2">Loading employees...</p>
    </div>

    <!-- Zero Employees to Reinvite State -->
    <div v-else-if="!isLoadingEmployees && employeesToReinviteCount === 0" class="text-center my-5">
      <h4>All good! Nothing to see here.</h4>
      <p>There are currently no employees who need a reinvitation.</p>
      <b-button variant="primary" :to="{ name: 'employees' }">Back to Employees</b-button>
    </div>

    <!-- Main Content: Employees to Reinvite Available -->
    <div v-else-if="!isLoadingEmployees && employeesToReinviteCount > 0">
      <div>
        <b-row>
          <b-col md="8">
            <div class="form-field">
              <span class="field-label">Subject:</span>
              Claim your Benefit
            </div>
            <div class="form-field">
              <span class="field-label">From:</span>
              {{ state.company.name }} via no-reply@stream.co
            </div>
            <div class="form-field">
              <span class="field-label">To:</span>
              {{
                employeesToReinviteCount === 1 ? '1 employee who has' : `${employeesToReinviteCount} employees who have`
              }}
              been eligible to Stream for > 3 mo but {{ employeesToReinviteCount === 1 ? 'has' : 'have' }} not signed up
              yet
              <a href="#" class="ml-2" @click.prevent="showEmployeeDetailsModal = true">See details</a>
            </div>
          </b-col>
          <b-col md="4" class="text-right">
            <b-button
              variant="primary"
              @click="showConfirmSendModal = true"
              :disabled="isLoading || employeesToReinviteCount === 0"
            >
              <b-spinner small v-if="isLoading"></b-spinner>
              Send
            </b-button>
          </b-col>
        </b-row>
      </div>

      <div class="mt-4">
        <h5>Email Preview:</h5>
        <div v-if="isLoadingHtml" class="text-center my-2">
          <b-spinner class="align-middle"></b-spinner>
        </div>
        <b-card v-else>
          <div v-safe-html:email="emailHtml"></div>
        </b-card>
      </div>
    </div>

    <!-- Employee Details Modal -->
    <b-modal v-model="showEmployeeDetailsModal" title="Employees to Reinvite" size="lg" hide-footer>
      <b-table
        striped
        class="mb-0"
        table-class="table-minimal"
        responsive
        show-empty
        :items="employeesToReinvite"
        :fields="employeeTableFields"
        :busy="isLoadingEmployees"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Loading...</strong>
          </div>
        </template>
      </b-table>
      <b-button class="mt-3" block @click="showEmployeeDetailsModal = false">Close</b-button>
    </b-modal>

    <!-- Confirm Send Modal -->
    <b-modal v-model="showConfirmSendModal" title="Confirm Send" @ok="sendReinvites" :ok-disabled="isLoading">
      <p>Click 'Send' to re-invite {{ employeesToReinviteCount }} employees to Stream.</p>
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()" :disabled="isLoading"> Cancel </b-button>
        <b-button variant="primary" @click="ok()" :disabled="isLoading">
          <b-spinner small v-if="isLoading"></b-spinner>
          Send
        </b-button>
      </template>
    </b-modal>
  </app-page>
</template>

<script>
import ApiClient from '@/ApiClient';
import AppPage from '@/components/AppPage.vue';
import State from '@/state/State';
import useAnalytics from '@/state/composables/useAnalytics';

export default {
  name: 'EmployeesReinvitePage',
  components: { AppPage },
  data() {
    return {
      state: State.state,
      employeesToReinvite: [],
      emailHtml: '',
      showEmployeeDetailsModal: false,
      showConfirmSendModal: false,
      isLoadingEmployees: false,
      isLoadingHtml: false,
      isSending: false,
      employeeTableFields: [
        { key: 'full_name', label: 'Name', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
      ],
    };
  },
  setup() {
    const { postAnalyticsEvent } = useAnalytics();
    return { postAnalyticsEvent };
  },
  computed: {
    employeesToReinviteCount() {
      return this.employeesToReinvite.length;
    },
    isLoading() {
      return this.isLoadingEmployees || this.isLoadingHtml || this.isSending;
    },
  },
  mounted() {
    this.postAnalyticsEvent({
      intent: 'view_employees_reinvite',
      task: 'reinvite_employees',
      action: 'view',
    });
    this.fetchEmployeesToReinvite();
    this.fetchEmailHtml();
  },
  methods: {
    async fetchEmployeesToReinvite() {
      this.isLoadingEmployees = true;
      try {
        const data = await ApiClient.getEmployeesToReinvite(this.state.company.company_id);
        this.employeesToReinvite = data.employees;
      } catch (error) {
        console.error('Error fetching employees to reinvite:', error);
        this.employeesToReinvite = [];
      } finally {
        this.isLoadingEmployees = false;
      }
    },
    async fetchEmailHtml() {
      this.isLoadingHtml = true;
      try {
        const data = await ApiClient.getEmployeeReinviteHtml(this.state.company.company_id);
        this.emailHtml = data.html;
      } catch (error) {
        console.error('Error fetching email HTML:', error);
        this.emailHtml = '<p>Error loading email preview.</p>';
      } finally {
        this.isLoadingHtml = false;
      }
    },
    async sendReinvites() {
      this.postAnalyticsEvent({
        intent: 'start_send_employees_reinvite',
        task: 'reinvite_employees',
        action: 'click',
      });
      this.isSending = true;
      this.showConfirmSendModal = false;
      try {
        await ApiClient.reinviteEmployees(this.state.company.company_id);
        this.postAnalyticsEvent({
          intent: 'complete_send_employees_reinvite',
          task: 'reinvite_employees',
          action: 'alert',
        });
        // Optionally: show success message, refresh data, etc.
        alert('Reinvitation emails sent successfully!'); // Placeholder for a nicer notification
        this.$router.push({ name: 'employees' });
      } catch (error) {
        this.postAnalyticsEvent({
          intent: 'error_send_employees_reinvite',
          task: 'reinvite_employees',
          action: 'alert',
        });
        console.error('Error sending reinvites:', error);
        alert('Failed to send reinvitation emails.'); // Placeholder for a nicer notification
      } finally {
        this.isSending = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.form-field {
  margin-bottom: 5px;
  .field-label {
    color: var(--palette-color-brand-primary);
    font-weight: bold;
  }
}
.large-spinner {
  width: 3rem;
  height: 3rem;
}
</style>
